import { z } from "zod";
import { collapseWhitespace, looksMisencoded } from "../text.js";
import { transferOfferSchema } from "./transfer.js";
/** A replacement character means the bytes were already wrong on arrival. */
const MISENCODED = "This text arrived damaged; check the encoding of what sent it.";
export const cellarRoleSchema = z.enum(["Owner", "Contributor", "Guest"]);
/**
 * A cellar as it appears in a list. `wineNumberCounter` is deliberately absent:
 * it is bookkeeping for the allocator, not something a screen shows.
 */
/**
 * The scale this cellar's own tasting notes are scored on. Carried with the
 * cellar rather than assumed by the interface: a score whose scale nobody
 * wrote down is unreadable later, which is exactly why a RatingSource carries
 * `scaleMax` for everybody else's numbers.
 */
export const tastingScaleSchema = z.object({
    max: z.number().int().positive(),
    hasDecimals: z.boolean(),
});
export const cellarSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    role: cellarRoleSchema,
    tastingScale: tastingScaleSchema,
    /**
     * Whether this reader may see what the wine cost — the answer, not the
     * inputs to it.
     *
     * The rule is the role and the reader's own `seesPrices` together, and it is
     * resolved on the server so that no screen has to know it and no screen can
     * get it wrong. Sending the setting instead would hand every page the same
     * small piece of reasoning to repeat, and the day one of them repeated it
     * wrong the cellar's prices would be on a screen.
     *
     * **The setting behind it went from one per cellar to one per reader on
     * 2026-09-22 and this field did not change at all**, which is what it was
     * built for: the answer travels, never the inputs to it, so a permission
     * axis could move without a single screen being consulted.
     *
     * Additive for one release, per the rule in CLAUDE.md, and the default was
     * `true` deliberately: a browser that arrived before the API was talking to
     * an API that was still sending prices to everyone, so defaulting to `false`
     * would have hidden the owner's own figures from them — the common case —
     * without withholding anything from anybody.
     *
     * **That release was `9.2`'s, on 2026-09-17, and the leniency stayed five
     * days too long.** It came off on the 22nd, found by walking the schemas
     * rather than by anything failing. The default was the most dangerous of
     * the eleven precisely because it was well chosen: `true` is right for the
     * owner and right for a browser ahead of its API, which is exactly why an
     * API that quietly stopped sending the field would have shown every reader
     * every price and looked correct doing it.
     */
    seesPrices: z.boolean(),
});
export const cellarListResponseSchema = z.object({
    cellars: z.array(cellarSummarySchema),
    /**
     * Cellars somebody is trying to hand to this account, from `9.8`.
     *
     * They ride on the list rather than being fetched on their own because this
     * is where they have to be seen: nothing is mailed, so the front page is the
     * only place an offer can reliably meet the person who has to answer it, and
     * a second request for a list that is almost always empty would be a second
     * thing to fail on the first screen after signing in.
     *
     * The list is sent on every request, so it is required here as of 10.7. The
     * default it carried through `9.8`'s release said *an API that has never
     * heard of a transfer*; kept any longer it would say *an API that forgot to
     * send them*, which is the one thing it must not be able to say quietly.
     */
    offers: z.array(transferOfferSchema),
});
/** Where a storage sits in the cellar's purpose, per project-scope.md. */
export const storagePurposeSchema = z.enum(["Serving", "Ageing", "Other"]);
/**
 * A name as it may be written down. Whitespace is collapsed on the way in,
 * because the uniqueness of a storage within a cellar is decided on the stored
 * string and `Kyl ` should not become a second cabinet beside `Kyl`.
 */
const writtenName = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().min(1).max(80))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED });
/**
 * Capacity is advisory everywhere it appears: it warns, it does not refuse.
 * Null is "not counted", which is different from zero.
 */
const capacity = z.number().int().positive().max(100_000).nullable();
/**
 * A cellar temperature, in whole degrees Celsius.
 *
 * Bounded because a number outside this range is a typo rather than a cellar:
 * nothing anybody stores wine in is below freezing or above thirty, and a
 * mistyped `120` would otherwise silently claim that every bottle in a cabinet
 * is cooked. Whole degrees, because that is what the dial says.
 *
 * Null is *not known*, and it is the honest default — on a shelf it means
 * "whatever the cabinet says", not that the two agree.
 */
const temperature = z.number().int().min(0).max(30).nullable();
export const shelfSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    capacity: z.number().int().nullable(),
    // Required since 10.7: the release this arrived in has long been serving, so
    // the default has done its work and an absent temperature is now drift
    // rather than an older API being honest.
    temperatureC: z.number().int().nullable(),
    /**
     * The last stocktake walk past this shelf, as a date; null when it has never
     * been walked. Only the list route fills it, because it is the stocktake's
     * shelf picker that asks. It is optional for one release so that the browser
     * can arrive before the API, and for the routes that return one cabinet.
     */
    lastWalkedAt: z.string().nullish(),
});
export const storageSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    purpose: storagePurposeSchema,
    capacity: z.number().int().nullable(),
    temperatureC: z.number().int().nullable(),
    shelves: z.array(shelfSummarySchema),
});
export const storageListResponseSchema = z.object({
    storages: z.array(storageSummarySchema),
});
export const createCellarRequestSchema = z.object({ name: writtenName });
export const createStorageRequestSchema = z.object({
    name: writtenName,
    purpose: storagePurposeSchema,
    capacity: capacity.default(null),
    temperatureC: temperature.default(null),
});
export const createShelfRequestSchema = z.object({
    name: writtenName,
    capacity: capacity.default(null),
    temperatureC: temperature.default(null),
});
/**
 * Setting the temperature of a cabinet or a shelf that already exists.
 *
 * Its own request rather than a general edit, because that is the whole of what
 * this needs: the two cabinets in this cellar were set up a year ago and the
 * field is new, so a create-only path would describe every cabinet except the
 * ones anybody owns. Renaming a cabinet or changing its purpose is a different
 * feature and nobody has asked for it.
 *
 * `null` is a meaning and not an omission — it is how a number that turned out
 * to be wrong is taken back, leaving *not known* rather than a stale figure.
 */
export const setTemperatureRequestSchema = z.object({
    temperatureC: temperature,
});
//# sourceMappingURL=cellar.js.map